import {
  ChevronDown,
  ChevronRight,
  Copy,
  FileText,
  List,
  Image as LucideImage,
  Pencil,
  Plus,
  Tag,
} from 'lucide-react'
import { useState } from 'react'
import { SiWordpress } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import type {
  WordPressActionsResult,
  WordPressGetContentResult,
  WordPressGetMediaResult,
  WordPressListCategoriesResult,
  WordPressListContentResult,
  WordPressListTagsResult,
  WordPressPerformTaskResult,
  WordPressPublishContentResult,
  WordPressUpdateContentResult,
  WordPressUpdateMediaResult,
  WordPressUploadMediaResult,
} from 'sprites-types/workflow/tools/wordpressActions'
import { wordpressActions } from 'sprites-types/workflow/tools/wordpressActions'
import { EnhancedMarkdown } from '../../ui/EnhancedMarkdown'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text)
}

function CollapsibleList({
  title,
  itemCount,
  children,
  defaultExpanded = true,
}: {
  title: string
  itemCount: number
  children: React.ReactNode
  defaultExpanded?: boolean
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  return (
    <div className="min-w-0 flex-1">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-2 text-sm font-medium text-gray-900 transition-colors hover:text-gray-700"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 flex-shrink-0" />
        )}
        <span>
          {title} ({itemCount})
        </span>
      </button>
      {isExpanded && <div className="mt-2 space-y-1.5">{children}</div>}
    </div>
  )
}

function GetContentDisplay({ data }: { data: WordPressGetContentResult }) {
  const contextActions = [
    {
      id: 'copy-url',
      label: 'Copy URL',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(data.url)
      },
    },
    {
      id: 'copy-id',
      label: 'Copy ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(String(data.id))
      },
    },
    {
      id: 'copy-json',
      label: 'Copy as JSON',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(JSON.stringify(data, null, 2))
      },
    },
  ]

  const isPost = data.contentType === 'post'

  return (
    <ContextMenuWrapper actions={contextActions}>
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm transition-all hover:shadow-md"
      >
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="mt-0.5 flex-shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
              <SiWordpress className="h-4 w-4 text-blue-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-900">{data.title}</span>
              {isPost && (
                <span className="rounded-md bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-800">
                  Post
                </span>
              )}
            </div>
            <div className="mt-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">{data.slug}</span>
                {data.status === 'draft' && (
                  <span className="rounded-md bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800">
                    Draft
                  </span>
                )}
                {data.status === 'private' && (
                  <span className="rounded-md bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
                    Private
                  </span>
                )}
              </div>
              <div className="mt-1 text-gray-500">ID: {data.id}</div>
            </div>
          </div>
        </div>
      </a>
    </ContextMenuWrapper>
  )
}

function ListContentDisplay({ data }: { data: WordPressListContentResult }) {
  const isPostList = data.contentType === 'post'

  return (
    <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="mt-0.5 flex-shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <List className="h-4 w-4 text-blue-600" />
          </div>
        </div>
        <CollapsibleList
          title={isPostList ? 'Posts' : 'Pages'}
          itemCount={data.total}
          defaultExpanded={data.items.length <= 10}
        >
          {data.items.map((item) => (
            <div
              key={item.id}
              className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs"
            >
              <div className="font-medium text-gray-900">{item.title}</div>
              <div className="text-gray-500">{item.slug}</div>
            </div>
          ))}
        </CollapsibleList>
      </div>
    </div>
  )
}

function PublishContentDisplay({ data }: { data: WordPressPublishContentResult }) {
  const contextActions = [
    {
      id: 'copy-url',
      label: 'Copy URL',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(data.url)
      },
    },
    {
      id: 'copy-id',
      label: 'Copy ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(String(data.id))
      },
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm transition-all hover:shadow-md"
      >
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="mt-0.5 flex-shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50">
              <Plus className="h-4 w-4 text-green-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <SiWordpress className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-900">{data.title}</span>
            </div>
            <div className="mt-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">{data.slug}</span>
                {data.status === 'draft' && (
                  <span className="rounded-md bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800">
                    Draft
                  </span>
                )}
                {data.status === 'private' && (
                  <span className="rounded-md bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
                    Private
                  </span>
                )}
              </div>
              <div className="mt-1 text-gray-500">ID: {data.id}</div>
            </div>
          </div>
        </div>
      </a>
    </ContextMenuWrapper>
  )
}

function UpdateContentDisplay({ data }: { data: WordPressUpdateContentResult }) {
  const contextActions = [
    {
      id: 'copy-url',
      label: 'Copy URL',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(data.url)
      },
    },
    {
      id: 'copy-id',
      label: 'Copy ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(String(data.id))
      },
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm transition-all hover:shadow-md"
      >
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="mt-0.5 flex-shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50">
              <Pencil className="h-4 w-4 text-orange-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <SiWordpress className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-900">{data.title}</span>
            </div>
            <div className="mt-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">{data.slug}</span>
                {data.status === 'draft' && (
                  <span className="rounded-md bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800">
                    Draft
                  </span>
                )}
                {data.status === 'private' && (
                  <span className="rounded-md bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
                    Private
                  </span>
                )}
              </div>
              <div className="mt-1 text-gray-500">ID: {data.id}</div>
            </div>
          </div>
        </div>
      </a>
    </ContextMenuWrapper>
  )
}

function UploadMediaDisplay({ data }: { data: WordPressUploadMediaResult }) {
  const contextActions = [
    {
      id: 'copy-url',
      label: 'Copy URL',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(data.url)
      },
    },
    {
      id: 'copy-id',
      label: 'Copy ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(String(data.id))
      },
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm transition-all hover:shadow-md"
      >
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="mt-0.5 flex-shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50">
              <LucideImage className="h-4 w-4 text-green-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">Media uploaded</span>
            </div>
            <div className="mt-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs">
              <div className="text-gray-500">
                ID: {data.id} • {data.mimeType}
                {data.width && data.height && ` • ${data.width}×${data.height}`}
              </div>
            </div>
          </div>
        </div>
      </a>
    </ContextMenuWrapper>
  )
}

function GetMediaDisplay({ data }: { data: WordPressGetMediaResult }) {
  const contextActions = [
    {
      id: 'copy-url',
      label: 'Copy URL',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(data.url)
      },
    },
    {
      id: 'copy-id',
      label: 'Copy ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(String(data.id))
      },
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm transition-all hover:shadow-md"
      >
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="mt-0.5 flex-shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
              <LucideImage className="h-4 w-4 text-blue-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">{data.title || 'Media'}</span>
            </div>
            <div className="mt-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs">
              <div className="text-gray-500">
                ID: {data.id} • {data.mimeType}
                {data.width && data.height && ` • ${data.width}×${data.height}`}
              </div>
            </div>
          </div>
        </div>
      </a>
    </ContextMenuWrapper>
  )
}

function UpdateMediaDisplay({ data }: { data: WordPressUpdateMediaResult }) {
  const contextActions = [
    {
      id: 'copy-url',
      label: 'Copy URL',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(data.url)
      },
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm transition-all hover:shadow-md"
      >
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="mt-0.5 flex-shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50">
              <LucideImage className="h-4 w-4 text-orange-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">Media updated</span>
            </div>
            <div className="mt-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs">
              <div className="text-gray-500">ID: {data.id}</div>
            </div>
          </div>
        </div>
      </a>
    </ContextMenuWrapper>
  )
}

function ListCategoriesDisplay({ data }: { data: WordPressListCategoriesResult }) {
  return (
    <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="mt-0.5 flex-shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <Tag className="h-4 w-4 text-blue-600" />
          </div>
        </div>
        <CollapsibleList
          title="Categories"
          itemCount={data.total}
          defaultExpanded={data.categories.length <= 10}
        >
          {data.categories.map((cat) => (
            <div
              key={cat.id}
              className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs"
            >
              <div className="font-medium text-gray-900">{cat.name}</div>
              <div className="text-gray-500">{cat.slug}</div>
            </div>
          ))}
        </CollapsibleList>
      </div>
    </div>
  )
}

function ListTagsDisplay({ data }: { data: WordPressListTagsResult }) {
  return (
    <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="mt-0.5 flex-shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <Tag className="h-4 w-4 text-blue-600" />
          </div>
        </div>
        <CollapsibleList
          title="Tags"
          itemCount={data.total}
          defaultExpanded={data.tags.length <= 10}
        >
          {data.tags.map((tag) => (
            <div
              key={tag.id}
              className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs"
            >
              <div className="font-medium text-gray-900">{tag.name}</div>
              <div className="text-gray-500">{tag.slug}</div>
            </div>
          ))}
        </CollapsibleList>
      </div>
    </div>
  )
}

function PerformTaskDisplay({ data }: { data: WordPressPerformTaskResult }) {
  return (
    <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="mt-0.5 flex-shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-50">
            <SiWordpress className="h-4 w-4 text-purple-600" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900">Task completed</div>
          <div className="mt-1.5">
            <EnhancedMarkdown className="text-xs">{data.summary}</EnhancedMarkdown>
          </div>
          {data.changes.length > 0 && (
            <div className="mt-3">
              <CollapsibleList
                title="Changes"
                itemCount={data.changes.length}
                defaultExpanded={data.changes.length <= 5}
              >
                {data.changes.map((change, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs"
                  >
                    <div className="font-medium text-gray-900">{change.action}</div>
                    <div className="text-gray-500">{change.details}</div>
                  </div>
                ))}
              </CollapsibleList>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function WordPressActionsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const inputs = wordpressActions.argsSchema.safeParse(nodeExecution.inputs.functionArgs)

  if (!inputs.success) {
    return <SchemaValidationError error={inputs.error} nodeExecution={nodeExecution} />
  }

  const schema = getToolResultSchema(wordpressActions, inputs.data)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const data = parseResult.data as WordPressActionsResult
  const actionName = inputs.data.action.name

  switch (actionName) {
    case 'getContent':
      return <GetContentDisplay data={data as WordPressGetContentResult} />
    case 'listContent':
      return <ListContentDisplay data={data as WordPressListContentResult} />
    case 'publishContent':
      return <PublishContentDisplay data={data as WordPressPublishContentResult} />
    case 'updateContent':
      return <UpdateContentDisplay data={data as WordPressUpdateContentResult} />
    case 'uploadMedia':
      return <UploadMediaDisplay data={data as WordPressUploadMediaResult} />
    case 'getMedia':
      return <GetMediaDisplay data={data as WordPressGetMediaResult} />
    case 'updateMedia':
      return <UpdateMediaDisplay data={data as WordPressUpdateMediaResult} />
    case 'listCategories':
      return <ListCategoriesDisplay data={data as WordPressListCategoriesResult} />
    case 'listTags':
      return <ListTagsDisplay data={data as WordPressListTagsResult} />
    case 'performTask':
      return <PerformTaskDisplay data={data as WordPressPerformTaskResult} />
    default:
      return (
        <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
          <div className="flex items-start gap-3 px-4 py-3">
            <div className="mt-0.5 flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-50">
                <SiWordpress className="h-4 w-4 text-gray-600" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900">WordPress action complete</div>
              <div className="mt-1 text-xs text-gray-500">Action: {actionName || 'unknown'}</div>
            </div>
          </div>
        </div>
      )
  }
}

function downloadWordPressActions(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function wordpressActionsToMarkdown(nodeExecution: NodeExecution): string {
  const inputs = wordpressActions.argsSchema.safeParse(nodeExecution.inputs.functionArgs)

  if (!inputs.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const schema = getToolResultSchema(wordpressActions, inputs.data)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const data = parseResult.data as WordPressActionsResult
  const actionName = inputs.data.action.name

  switch (actionName) {
    case 'getContent': {
      const content = data as WordPressGetContentResult
      return `## WordPress ${content.contentType === 'post' ? 'Post' : 'Page'}: ${content.title}

**URL:** ${content.url}
**Status:** ${content.status}
**Slug:** ${content.slug}
**ID:** ${content.id}

---
`
    }
    case 'listContent': {
      const list = data as WordPressListContentResult
      const items = list.items
        .map((item) => `- **${item.title}** (${item.slug}) - ID: ${item.id}`)
        .join('\n')
      return `## WordPress ${list.contentType === 'post' ? 'Posts' : 'Pages'} (${list.total})

${items}

---
`
    }
    case 'publishContent': {
      const content = data as WordPressPublishContentResult
      return `## WordPress Content Published

**Title:** ${content.title}
**URL:** ${content.url}
**Status:** ${content.status}
**Slug:** ${content.slug}
**ID:** ${content.id}

---
`
    }
    case 'updateContent': {
      const content = data as WordPressUpdateContentResult
      return `## WordPress Content Updated

**Title:** ${content.title}
**URL:** ${content.url}
**Status:** ${content.status}
**Slug:** ${content.slug}
**ID:** ${content.id}

---
`
    }
    case 'uploadMedia': {
      const media = data as WordPressUploadMediaResult
      return `## WordPress Media Uploaded

**URL:** ${media.url}
**ID:** ${media.id}
**Type:** ${media.mimeType}

---
`
    }
    case 'getMedia': {
      const media = data as WordPressGetMediaResult
      return `## WordPress Media

**Title:** ${media.title || 'Untitled'}
**URL:** ${media.url}
**ID:** ${media.id}
**Type:** ${media.mimeType}

---
`
    }
    case 'updateMedia': {
      const media = data as WordPressUpdateMediaResult
      return `## WordPress Media Updated

**ID:** ${media.id}
**URL:** ${media.url}

---
`
    }
    case 'listCategories': {
      const cats = data as WordPressListCategoriesResult
      const items = cats.categories.map((cat) => `- **${cat.name}** (${cat.slug})`).join('\n')
      return `## WordPress Categories (${cats.total})

${items}

---
`
    }
    case 'listTags': {
      const tags = data as WordPressListTagsResult
      const items = tags.tags.map((tag) => `- **${tag.name}** (${tag.slug})`).join('\n')
      return `## WordPress Tags (${tags.total})

${items}

---
`
    }
    case 'performTask': {
      const task = data as WordPressPerformTaskResult
      const changes = task.changes
        .map((change) => `- **${change.action}**: ${change.details}`)
        .join('\n')
      return `## WordPress Task Completed

**Summary:** ${task.summary}

### Changes Made:
${changes}

---
`
    }
    default:
      return `## WordPress Action: ${actionName}

\`\`\`json
${JSON.stringify(data, null, 2)}
\`\`\`

---
`
  }
}

export const WordPressActionsDisplay = createWorkflowToolComponent(
  WordPressActionsDisplayComponent,
  downloadWordPressActions,
  wordpressActionsToMarkdown
)
